import React, { useState, useEffect } from 'react';
import { environmentService, type IntegrationStatus, type EnvVarStatus } from '../services/environmentService';

export const EnvironmentStatus: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [integrations, setIntegrations] = useState<IntegrationStatus[]>([]);
  const [envVars, setEnvVars] = useState<EnvVarStatus[]>([]);
  const [systemHealth, setSystemHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      setLoading(true);
      try {
        const [integrationsData, healthData] = await Promise.all([
          environmentService.checkIntegrations(),
          environmentService.getSystemHealth()
        ]);
        const varsData = environmentService.checkAllEnvironmentVariables();
        
        setIntegrations(integrationsData);
        setEnvVars(varsData);
        setSystemHealth(healthData);
      } catch (error) {
        console.error('Failed to check environment status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
    // Refresh every 30 seconds
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'configured': return 'text-green-400';
      case 'partial': return 'text-yellow-400';
      case 'not-configured': return 'text-gray-400';
      case 'error': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'configured': return '✅';
      case 'partial': return '⚠️';
      case 'not-configured': return '❌';
      case 'error': return '🔴';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-800/60 p-4 rounded-lg border border-slate-700 mb-4">
        <div className="flex items-center">
          <svg className="animate-spin h-5 w-5 mr-2 text-indigo-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          <span className="text-slate-300">환경 설정 확인 중...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/60 p-4 rounded-lg border border-slate-700 mb-4">
      {/* Summary Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">
            시스템 상태
          </h3>
          <p className={`text-sm ${systemHealth?.healthy ? 'text-green-400' : 'text-yellow-400'}`}>
            {systemHealth?.summary}
          </p>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-all"
        >
          {isExpanded ? '숨기기' : '자세히 보기'}
        </button>
      </div>

      {/* Integration Status Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
        {integrations.map(integration => (
          <div 
            key={integration.name}
            className="bg-slate-900/50 p-2 rounded border border-slate-700"
            title={integration.message}
          >
            <div className="flex items-center gap-1">
              <span className="text-lg">{getStatusIcon(integration.status)}</span>
              <span className="text-xs text-slate-300 truncate">
                {integration.displayName}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed View */}
      {isExpanded && (
        <div className="mt-4 space-y-4">
          {/* Critical Errors */}
          {systemHealth?.criticalErrors?.length > 0 && (
            <div className="bg-red-900/30 border border-red-700/50 rounded p-3">
              <h4 className="text-red-400 font-medium mb-2">⚠️ 중요 오류</h4>
              <ul className="text-sm text-red-300 space-y-1">
                {systemHealth.criticalErrors.map((error: string, i: number) => (
                  <li key={i}>• {error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Warnings */}
          {systemHealth?.warnings?.length > 0 && (
            <div className="bg-yellow-900/30 border border-yellow-700/50 rounded p-3">
              <h4 className="text-yellow-400 font-medium mb-2">📌 경고</h4>
              <ul className="text-sm text-yellow-300 space-y-1">
                {systemHealth.warnings.map((warning: string, i: number) => (
                  <li key={i}>• {warning}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Integration Details */}
          <div>
            <h4 className="text-slate-300 font-medium mb-2">연동 상태 상세</h4>
            <div className="space-y-2">
              {integrations.map(integration => (
                <div 
                  key={integration.name}
                  className="bg-slate-900/50 p-3 rounded border border-slate-700"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={getStatusColor(integration.status)}>
                        {integration.displayName}
                      </span>
                    </div>
                    <span className="text-xs text-slate-400">
                      {integration.message}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Environment Variables */}
          <div>
            <h4 className="text-slate-300 font-medium mb-2">환경 변수 상태</h4>
            <div className="bg-slate-900/50 rounded border border-slate-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-2 text-slate-400">변수명</th>
                    <th className="text-center p-2 text-slate-400">상태</th>
                    <th className="text-left p-2 text-slate-400">값</th>
                  </tr>
                </thead>
                <tbody>
                  {envVars.map(envVar => (
                    <tr key={envVar.name} className="border-b border-slate-700/50">
                      <td className="p-2">
                        <div>
                          <div className="text-slate-300 font-mono text-xs">
                            {envVar.name}
                          </div>
                          <div className="text-slate-500 text-xs">
                            {envVar.description}
                          </div>
                        </div>
                      </td>
                      <td className="text-center p-2">
                        {envVar.present ? (
                          <span className="text-green-400">✅</span>
                        ) : (
                          <span className="text-gray-400">❌</span>
                        )}
                      </td>
                      <td className="p-2">
                        <span className="text-slate-400 font-mono text-xs">
                          {envVar.value || '미설정'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Backend Status Note */}
          <div className="bg-slate-900/50 p-3 rounded border border-slate-700">
            <p className="text-xs text-slate-400">
              💡 백엔드 환경 변수(NOTION_TOKEN, GOOGLE_CLOUD_API_KEY 등)는 
              보안상 서버에서만 확인 가능합니다. 위 연동 상태로 작동 여부를 확인하세요.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};